import {
	cloneDeep,
	shuffle,
	zip,
	isEqual,
	mapValues
} from "lodash";
import { useSelector } from "react-redux";

export function nukeDisplayPlacement(placementId) {
	try{
		if (placementId && window[`ft${placementId}`] && document.getElementById(`ftdiv${placementId}`)) {
			window[`ft${placementId}`].nuke();
			delete window[`ft${placementId}`];
		}
	} catch(e){
		console.error(e);
	}
}

export function renderDisplayPlacement(displayData) {
	let placementId = displayData?.placement?.id;

	nukeDisplayPlacement(placementId);

	if (placementId && displayData) {
		if (window.ftUtils) {
			window[`ft${placementId}`] = cloneDeep(window.ftUtils.common);
			window[`ft${placementId}`].configMerge(displayData);
		} else {
			console.log("ftUtils not loaded");
			// getFtUtils((ftUtils) => {
			// 	if (!ftUtils) {
			// 		let errorMessage = `Failed to load ftUtils. FT_UTILS_URL: ${FT_UTILS_URL}`;
			// 		tellSentry(errorMessage);
			// 		throw new Error(errorMessage);
			// 	} else {
			// 		window[`ft${placementId}`] = cloneDeep(window.ftUtils.common);
			// 		window[`ft${placementId}`].configMerge(displayData);
			// 	}
			// });
		}
	}
}

export function getVariableBounds(
	sourceVersions,
	options = {
		minMultiplier: 0.8,
		maxMultiplier: 1.2,

		minDefault: 20,
		maxDefault: 100,
	}
){
	/*
		This function computes min/max length bounds for text variables, and returns a hash,
		keyed by the variable name.

		e.g.
		```
		{
			cta: {
				min: 7,
				max: 12,
			}
		}
		```

		Bounds are computed based on the average length of values found in the sourceVersions
		for a given variable, plus/minus 20% by default, but this can be configured via
		`options.minMultiplier` and `options.maxMultiplier`.

		If source version values are empty strings, their values aren't figured into the average.
		If there are only empty strings for a variable, `options.minDefault` and `options.maxDefault`
		will be used instead.
	*/
	const lengthsByVariable = {};
	sourceVersions.forEach((version) => {
		Object.entries(version.variableConfig).forEach(([variableName, value]) => {
			if(/text/i.test(value.type)){
				if(!lengthsByVariable[variableName]){
					lengthsByVariable[variableName] = [];
				}
				if(value.data.length){
					// If a value is empty, don't factor it into the average
					lengthsByVariable[variableName].push(value.data.length);
				}
			}
		});
	});

	const boundsByVariable = mapValues(lengthsByVariable, (lengths) => {
		const total = lengths.reduce((sum, length) => sum + length, 0);
		const average = lengths.length ? total/lengths.length : 0; /*
			If we discard values, there's a chance there are no lengths, so we use a
			ternary to avoid dividing by zero and getting Infinity.

			If there are no lengths, we set the average to zero so we can just replace
			it with the minDefault/maxDefault.
		*/

		return {
			min: Math.round(average * options.minMultiplier) || options.minDefault,
			max: Math.round(average * options.maxMultiplier) || options.maxDefault,
		};
	});

	return boundsByVariable;
}

export function getTextWithBounds(variable){
	const name = variable?.name || "";
	if(/cta/i.test(name)){
		return {
			name,
			min: 5,
			max: 20,
		};
	}
	return {
		name,
		min: 20,
		max: 100,
	};
}

// Bedrock API requires these to be multiples of 64
const DEFAULT_WIDTH = 1024;
const DEFAULT_HEIGHT = 1024;

export function getImageWithDimensions(variable){
	const name = variable?.name || "";
	// const { width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT } = variable?.cropMetadata?.asset || {};
	const width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT;
	return {
		name,
		width,
		height,
	};
}

export function useVariableBounds(){
	/*
		This returns functions that can be used to prepare a generateAssets request by adding
		the min/max length parameters for text variables, and the width/height parameters for image variables.

		For texts, we use precalculated bounds for each variable name, computed based on
		the source version values, found in the store.
	*/
	const boundsByVariable = useSelector(state => state.versionGeneration.boundsByVariable);
	function getTextWithBounds(variable){
		return {
			name: variable.name,
			...boundsByVariable[variable.name],
		};
	}

	return {
		getTextWithBounds,
		getImageWithDimensions,
	};
}

export function combine(variable, combinations = [{}]){
	/*
		variable is an object in the format:
		```
			{
				name: "cta",
				values: [
					{ value: "cta 1" },
					{ value: "cta 2" },
				],
			}
		```

		combinations is an array of objects in the format:
		```
			[
				{
					headline: "headline 1",
				},
				{
					headline: "headline 2",
				},
			]
		```

		We essentially want to produce a cartesian product of the two sets of values
		by combining each item in `variable.values` with each item in combinations, returning
		a new set of combinations. e.g. with the values above:
		```
			[
				{
					cta: "cta 1",
					headline: "headline 1",
				},
				{
					cta: "cta 1",
					headline: "headline 2",
				},
				{
					cta: "cta 2",
					headline: "headline 1",
				},
				{
					cta: "cta 2",
					headline: "headline 2",
				},
			]
		```
	*/
	const { name, values } = variable || {};
	if(!values || !values.length){
		return combinations;
	}

	return values.reduce((prev, value) => {
		const variableValue = value?.value || value?.src;
		return prev.concat(combinations.map((combination) => {
			return {
				...combination,
				[name]: variableValue,
			};
		}));
	}, []);
}

export function generateVariations(generatedAssets){
	/*
		generatedAssets is in the format:
		```
			{
				texts: [
					{
						name: "headline",
						values: [
							{ value: "headline 1" },
							...
						],
					},
					...
				],
				images: [
					{
						name: "background_image",
						values: [
							{ src: "http://path.to/background1.jpg" },
							...
						],
					},
					...
				],
			}
		```

		There's no guarantee that the variables have the same number of generated values, or will
		have any values.

		We want to generate all combinations of values, and return them in an order that maximizes
		the visual variety

		Given that we only allow the selection of one image variable, we can do this fairly simply by
		producing each possible combination, grouped by image, and then essentially taking one item from
		each group in order, until all groups are empty.

		This should guarantee that consecutive variations have different images, assuming more
		than one item in `generatedAssets.images[0].values`
	*/

	const { texts = [], images = []} = generatedAssets || {};
	const textCombinations = texts.reduce((prev, text) => {
		return combine(text, prev);
	}, undefined);

	if(images.length === 0){
		return textCombinations;
	}

	// Assume there's only one image variable
	const imageVariableName = images[0]?.name;

	/*
		We create a variable object with a single image value and pass it to `combine`, to keep
		the images grouped in separate arrays, which we shuffle so the text combinations aren't
		in a completely predictable order.
	*/
	const shuffledCombinationsByImage = images[0]?.values?.map((image) => {
		return shuffle(combine({
			name: imageVariableName,
			values: [image],
		}, textCombinations));
	});

	/*
		`shuffledCombinationsByImage` is an array of arrays, but `zip` expects each array as an argument,
		so we spread it, and then flatten the result into a single array of variations
	*/
	return zip(...shuffledCombinationsByImage).flat();
}


/* Selection utils */

export function findVariation(variations = [], target){
	return variations.find(value => isEqual(value, target));
}

export function addSelectedVersion(selectedVersions = {}, creativeId, variation){
	/*
		We want to organize the selections in a way that makes it easy to prepare
		the API request, which is organized by creative, with a list of versions.

		We instead organize by creative with a list of variations (just the
		variable names and a string value), rather than the full `variableConfig` formatting.
		This makes it easier to compare.

		We do this assuming that the user won't be able to make selections with one source
		version, and then switch to another and make more selections.  We just assume that
		it's safe to use whatever the selected source versions are uniformly at submission time.

		selectedVersions is in the format:
		```
			{
				"123": { // creativeId
					variations: [
						{
							image1: "http://whatever.url",
							cta: "Use AI",
							...
						},
						...
					]
				}
			}
		```
	*/
	const existingCreative = selectedVersions[`${creativeId}`];
	const variations = [...(existingCreative?.variations || [])];

	if(!findVariation(variations, variation)){
		variations.push(variation);
	}

	return {
		...selectedVersions,
		[`${creativeId}`]: { variations },
	};
}

export function removeSelectedVersion(selectedVersions, creativeId, variation){
	const existingCreative = selectedVersions[`${creativeId}`];
	const variations = (existingCreative?.variations || []).filter(item => !isEqual(item, variation));

	return {
		...selectedVersions,
		[`${creativeId}`]: { variations },
	};
}
